import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@/lib/utils'

/**
 * Aurora Tabs — segmented control style.
 *
 * The list has a thin hairline rule under it, and the active trigger gets
 * an Aurora-violet underline. This reads as document chrome rather than the
 * pill-segmented style we had before (which always made the page feel like
 * "settings", not "content").
 */

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-3', className)}
      {...props}
    />
  )
}

type TabsListProps = React.ComponentProps<typeof TabsPrimitive.List> & {
  wrap?: boolean
}

function TabsList({
  className,
  wrap = false,
  ...props
}: TabsListProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)

  const scrollBy = React.useCallback((delta: number) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: delta, behavior: 'smooth' })
  }, [])

  // The wrapper owns horizontal overflow so long tab strips stay reachable
  // when the work area is narrower than the full app viewport.
  return (
    <div
      ref={scrollRef}
      data-slot="tabs-scroll"
      tabIndex={0}
      onWheel={(event) => {
        const el = scrollRef.current
        if (!el) return

        const horizontalDelta =
          Math.abs(event.deltaX) > Math.abs(event.deltaY)
            ? event.deltaX
            : event.deltaY
        if (horizontalDelta === 0) return

        const maxScroll = el.scrollWidth - el.clientWidth
        if (maxScroll <= 0) return

        const next = Math.max(0, Math.min(maxScroll, el.scrollLeft + horizontalDelta))
        if (next === el.scrollLeft) return

        event.preventDefault()
        el.scrollLeft = next
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return

        if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollBy(80)
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollBy(-80)
        } else if (event.key === 'End') {
          event.preventDefault()
          const el = scrollRef.current
          if (el) el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' })
        } else if (event.key === 'Home') {
          event.preventDefault()
          const el = scrollRef.current
          if (el) el.scrollTo({ left: 0, behavior: 'smooth' })
        }
      }}
      className={cn(
        'relative min-w-0 max-w-full shrink-0 pb-1 outline-none',
        'focus-visible:ring-[3px] focus-visible:ring-ring/30',
        wrap
          ? 'overflow-visible'
          : [
              'overflow-x-auto overscroll-x-contain',
              '[scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent',
              "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-8 after:bg-gradient-to-l after:from-background after:to-transparent after:content-['']",
            ],
      )}
    >
      <TabsPrimitive.List
        data-slot="tabs-list"
        className={cn(
          'relative flex min-w-full items-center gap-0.5 border-b border-border text-muted-foreground',
          wrap
            ? 'h-auto w-full flex-wrap gap-x-1 gap-y-1 pb-2'
            : 'h-10 w-max pr-8',
          className,
        )}
        {...props}
      />
    </div>
  )
}

function TabsTrigger({
  className,
  onClick,
  onFocus,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const localRef = React.useRef<HTMLButtonElement | null>(null)
  const revealTrigger = React.useCallback(() => {
    if (typeof localRef.current?.scrollIntoView !== 'function') return
    localRef.current.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    })
  }, [])

  return (
    <TabsPrimitive.Trigger
      ref={(node) => {
        localRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      }}
      data-slot="tabs-trigger"
      className={cn(
        'relative inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap px-3 text-sm font-medium tracking-tight transition-colors',
        "after:absolute after:inset-x-2.5 after:-bottom-px after:h-[2px] after:rounded-full after:bg-transparent after:content-['']",
        'hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 focus-visible:ring-offset-0',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:text-foreground data-[state=active]:after:bg-primary',
        className,
      )}
      onClick={(event) => {
        revealTrigger()
        onClick?.(event)
      }}
      onFocus={(event) => {
        revealTrigger()
        onFocus?.(event)
      }}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
